import CircularProgress from "@mui/material/CircularProgress";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AlertWrapper from "~/src/components/Alerts/AlertWrapper";
import { BottomBarDouble } from "~/src/components/BottomBars/BottomBars";
import SoSCard from "~/src/components/Card/SoSCard";
import ErrorList from "~/src/components/Form/ErrorList";
import { CompleteIcon, DeleteIcon, EditIcon, TargetIcon } from "~/src/components/Icons/Icons";
import Text, { TextVariant } from "~/src/components/Text/Text";
import { StorageContext, StorageItem } from "~/src/contexts/StorageContext/StorageProvider";
import useJaktdataHelper from "~/src/contexts/StorageContext/useJaktdataHelper";
import UIContext from "~/src/contexts/UIContext/UIProvider";
import { useClientState } from "~/src/hooks/useClientState";
import { useReload } from "~/src/hooks/useReload";
import { ApiError, Individ, IndividResponse, Jaktdag, Område } from "../../api/types";
import { jaktdataApi } from "../../appGlobals";
import AlertDefault, { AlertVariant } from "../../components/Alerts/AlertDefault";
import ButtonDefault, { ButtonVariant } from "../../components/Button/ButtonDefault";
import ShowMoreToggle from "../../components/ShowMoreToggle";
import AppDataContext from "../../contexts/AppDataContext/DataProvider";
import { getKjonnOgAlderByArt } from "../../helpers/artHelper";
import { getOmradeForIndivid } from "../../helpers/omradeHelper";
import { hentJaktaar, toStringWithFullMonth } from "../../helpers/time";
import { AlderType, ArtType, KjønnType } from "../../helpers/types";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import { useStateful } from "../../hooks/useStateful";
import { getJaktdagQuery, getSkuttDyrQuery } from "../../react-query/queries";
import { MalemetodeSlaktevekt } from "../Registrering/types";
import TomtResultat from "./Components/TomtResultat";
import { getReadableEnumLowercase, isMissingOvervakData } from "./helpers";

export default function SkuttDyrDetaljer({
  setHeader,
}: {
  setHeader: (e: string) => void;
}): JSX.Element {
  const tBase = "pages.oversikt.skuttDyrDetaljer.";
  const { individId } = useParams();
  const navigate = useNavigate();
  const appData = useContext(AppDataContext);
  const queryClient = useQueryClient();
  const ui = useContext(UIContext);
  const omrade = useStateful<Område | null | undefined>(null);
  const isRegByOther = useStateful(false);
  const canEditIndivid = useStateful(false);
  const ikkeVeid = `${t("pages.registrerArt.omSkuttDyr.ikkeVeid")}`;
  const storage = useContext(StorageContext);
  const [skuttDyr, setSkuttDyr] = useState<IndividResponse | null | undefined>(
    queryClient.getQueryData(["individId-" + individId, { individId: individId }]),
  );
  const [storedIndivid, setStoredIndivid] = useState<StorageItem<Individ> | null | undefined>(null);
  const reload = useReload("SkuttDyrDetaljer");
  const { refetchJaktdataForJaktomrade } = useJaktdataHelper();
  const clientState = useClientState();
  const isOnline = clientState === "Online";
  const isDeleting = useStateful(false);

  async function refresh(skuttDyr: IndividResponse) {
    skuttDyr.Jaktfelt && (await refetchJaktdataForJaktomrade(skuttDyr.Jaktfelt));
    skuttDyr.Villreinvald && (await refetchJaktdataForJaktomrade(skuttDyr.Villreinvald));
    skuttDyr.Vald && (await refetchJaktdataForJaktomrade(skuttDyr.Vald));
  }

  const skuttDyrResult = useQuery({
    meta: { url: `/individer/${individId}` },
    queryKey: ["individId-" + individId, { individId: individId }],
    queryFn: getSkuttDyrQuery,
    retry: 1,
    enabled: storage.get<Individ>({ id: individId }, "Individ") === undefined && !isDeleting.value,
  });

  useEffect(() => {
    // use stored individ if it exists
    // (newly created individ only exists in storage, and edited should use stored even if API is available)
    const storedIndivid = storage.get<Individ>({ id: individId }, "Individ");

    // need to show sync status for all operation types, but only set skuttDyr if not delete, because stored object only contains id
    if (storedIndivid) setStoredIndivid(storedIndivid);
    if (storedIndivid && storedIndivid.operation !== "delete") {
      setSkuttDyr(storedIndivid?.data);
    } else if (skuttDyrResult.isSuccess && typeof skuttDyrResult.data !== "string") {
      setSkuttDyr(skuttDyrResult.data);
      setStoredIndivid(undefined);
    }
  }, [skuttDyrResult.data, isOnline, appData.sync.isSynced.value]);

  const jaktdag = useQuery({
    meta: { url: `/jaktdager/${skuttDyr?.JaktdagId}` },
    queryKey: ["jaktdagId-" + skuttDyr?.JaktdagId, { jaktdagId: skuttDyr?.JaktdagId }],
    queryFn: getJaktdagQuery,
    enabled:
      !!skuttDyr?.JaktdagId &&
      storage.get<Jaktdag>({ id: skuttDyr?.JaktdagId }, "Jaktdag") === undefined,
  }).data;

  useEffect(() => {
    if (!skuttDyr) return;
    omrade.set(getOmradeForIndivid(skuttDyr, appData));
    isRegByOther.set(appData.jegeropplysninger.data?.Jegernummer !== skuttDyr?.Jegernummer);
    canEditIndivid.set(!skuttDyr?.Godkjent);
  }, [skuttDyr]);

  const handleDeleteClick = () => {
    const regByOtherText = (
      <Text>
        {t(tBase + "deleteDialogMessageRegByOther1")}
        <strong className="font-bold">{skuttDyr?.RegistrertAv}</strong>
        {t(tBase + "deleteDialogMessageRegByOther2")}
      </Text>
    );
    const hasCWDProvesvar =
      skuttDyr?.CWDPrøvesvar === "Positiv" || skuttDyr?.CWDPrøvesvar === "Negativ";
    deleteDialog.show({
      message: hasCWDProvesvar
        ? t(tBase + "deleteDialogMessageCWD")
        : isRegByOther.value
          ? regByOtherText
          : t(tBase + "deleteDialogMessage"),
    });
  };

  const handleConfirmDelete = () => {
    if (!skuttDyr?.Id) return;
    isDeleting.set(true);

    const storedIndivid = storage.get<Individ>({ id: skuttDyr.Id }, "Individ");

    const removeIndividFromCache = (individId: string | undefined) => {
      if (!skuttDyr?.Id) return;

      const sesong = hentJaktaar();
      for (const areaId of [skuttDyr.Jaktfelt, skuttDyr.Vald, skuttDyr.Villreinvald].filter(
        Boolean,
      )) {
        const queryKey = ["skuttedyr", { jaktomradeId: areaId, sesong }];
        const existing = queryClient.getQueryData<{ data: { Id?: string } }[]>(queryKey);
        if (existing) {
          queryClient.setQueryData(
            queryKey,
            existing.filter((i) => i.data?.Id !== individId),
          );
        }
      }
    };

    if (storedIndivid?.operation === "create" && storedIndivid.syncStatus !== "unconfirmed") {
      storage.delete({ id: skuttDyr.Id }, "Individ");
      removeIndividFromCache(skuttDyr.Id);
      ui.showSnackbar({
        message: t("pages.oversikt.skuttDyrDetaljer.skuttDyrDeletedMessage"),
        severity: AlertVariant.Success,
      });
      isDeleting.set(false);
      navigate("/oversikt");
      return;
    }

    jaktdataApi
      .deleteIndivid(skuttDyr.Id)
      .then(() => {
        storage.delete({ id: skuttDyr.Id }, "Individ");
        if (skuttDyr.JaktdagId && skuttDyr.JaktdagId != "") {
          const jaktdagQueryKey = { queryKey: ["jaktdag", { jaktdagId: skuttDyr.JaktdagId }] };
          queryClient.invalidateQueries(jaktdagQueryKey).then(() => {
            queryClient.refetchQueries(jaktdagQueryKey).then(() => {
              refresh(skuttDyr).then(() => {
                ui.showSnackbar({
                  message: t("pages.oversikt.skuttDyrDetaljer.skuttDyrDeletedMessage"),
                  severity: AlertVariant.Success,
                });
                isDeleting.set(false);
                navigate("/oversikt");
              });
            });
          });
        } else {
          refresh(skuttDyr).then(() => {
            ui.showSnackbar({
              message: t("pages.oversikt.skuttDyrDetaljer.skuttDyrDeletedMessage"),
              severity: AlertVariant.Success,
            });
            isDeleting.set(false);
            navigate("/oversikt");
          });
        }
      })
      .catch((e: ApiError) => {
        if (e.axiosError?.code === "ERR_NETWORK" || e.axiosError?.code === "ECONNABORTED") {
          storage.add({
            objectType: "Individ",
            objectKey: { id: skuttDyr.Id },
            data: skuttDyr,
            isPending: true,
            operation: "delete",
            lastUpdated: new Date(),
          });

          removeIndividFromCache(skuttDyr?.Id);

          ui.showSnackbar({
            message: t("sync.noNetworkWillSyncLater"),
            severity: AlertVariant.Info,
          });
          isDeleting.set(false);
          navigate("/oversikt");
          return;
        }
        refresh(skuttDyr).then(() => {
          isDeleting.set(false);
          ui.showSnackbar({ message: e.message, severity: AlertVariant.Warning });
          navigate("/oversikt");
        });
      });
  };

  const deleteDialog = useConfirmationDialog({
    title: t(tBase + "deleteDialogTitle"),
    message: "",
    onConfirm: handleConfirmDelete,
    confirmButtonText: t("actions.delete"),
  });

  useEffect(() => {
    if (skuttDyr) {
      setHeader(`${skuttDyr?.Art} ${getKjonnOgAlderByArt(skuttDyr)}`);
    }
  }, [skuttDyr]);

  const removeStoredIndivid = () => {
    if (!storedIndivid) {
      return;
    }
    storage.delete(storedIndivid.objectKey, "Individ");
    reload.reload();
  };

  const endreIndivid = () => navigate("/registrering/fellingsinfo/" + skuttDyr?.Id);

  const showLastSynced =
    !isOnline || (isOnline && storage.anyPending() && appData.sync.lastSynced.value !== undefined);

  if ((isDeleting.value || skuttDyrResult.isFetching) && !skuttDyr) {
    return (
      <TomtResultat>
        <CircularProgress thickness={7} size={80} />
      </TomtResultat>
    );
  }

  return (
    <div className="flex flex-col justify-between h-full">
      <div className="m-4 mb-0 pb-4">
        {!skuttDyr && (
          <TomtResultat className="m-4">{t("errors.notFound", { type: "Individ" })}</TomtResultat>
        )}
        {skuttDyr && (
          <>
            <AlertWrapper>
              {jaktdag?.Godkjent && canEditIndivid.value && (
                <AlertDefault variant={AlertVariant.Success}>
                  {t(tBase + "jaktdagenErGodkjent")}
                </AlertDefault>
              )}
              {!canEditIndivid.value && (
                <AlertDefault variant={AlertVariant.Info}>
                  {skuttDyr.Art !== ArtType.Villrein
                    ? t(tBase + "kanIkkeEndres")
                    : t(tBase + "kanIkkeEndresVillrein")}
                </AlertDefault>
              )}
              {skuttDyr.UtenforJakttid && (
                <AlertDefault variant={AlertVariant.Warning}>
                  {t(tBase + "utenforJakttid")}
                </AlertDefault>
              )}

              {isMissingOvervakData(skuttDyr) && (
                <AlertDefault variant={AlertVariant.Warning}>
                  {t(tBase + "skuttIOvervakomrade")}
                </AlertDefault>
              )}

              {storedIndivid?.isPending && showLastSynced && (
                <AlertDefault variant={AlertVariant.Sync}>{t("sync.pending")}</AlertDefault>
              )}

              {storedIndivid?.syncStatus === "error" && (
                <AlertDefault
                  variant={AlertVariant.SyncError}
                  bottomElement={
                    isOnline && storedIndivid?.operation != "create" ? (
                      <ButtonDefault
                        id="fjernEndringer"
                        onClick={removeStoredIndivid}
                        label={t("actions.discardAndRestore")}
                      />
                    ) : (
                      <></>
                    )
                  }
                >
                  <Text variant={TextVariant.small}>{t("sync.syncError")}</Text>
                  {storedIndivid.errors && <ErrorList errors={storedIndivid.errors} />}
                </AlertDefault>
              )}
            </AlertWrapper>

            <SoSCard
              titleString={`${skuttDyr.Art} ${getKjonnOgAlderByArt(skuttDyr)}`}
              titleIcon={<TargetIcon className="text-ldir-blue-900" ariaHidden />}
              cardFields={[
                {
                  label: t(tBase + "jaktomrade"),
                  value: omrade.value?.Navn + " " + omrade.value?.Id,
                },
                {
                  label: t(tBase + "terreng"),
                  value: skuttDyr.Terreng,
                  show: skuttDyr.Art === ArtType.Hjort,
                },
                {
                  label: t("pages.registrerArt.skutt"),
                  value: toStringWithFullMonth(skuttDyr.Dato),
                },
                {
                  label: t(tBase + "slaktevekt"),
                  warning: skuttDyr.SlaktevektKg === null || skuttDyr.SlaktevektKg === undefined,
                  value: skuttDyr.SlaktevektKg
                    ? `${skuttDyr.SlaktevektKg} kg, ${getReadableEnumLowercase(
                        skuttDyr.MålemetodeSlaktevekt,
                      )}`
                    : skuttDyr.MålemetodeSlaktevekt === MalemetodeSlaktevekt.IkkeVeid
                      ? ikkeVeid
                      : t(tBase + "ikkeRegistrert"),
                },
                {
                  label: t(tBase + "merkelappnr"),
                  warning:
                    skuttDyr.Merkelapp === null ||
                    skuttDyr.Merkelapp === undefined ||
                    skuttDyr.Merkelapp === "",
                  value: skuttDyr.Merkelapp ? skuttDyr.Merkelapp : t(tBase + "ikkeRegistrert"),
                },
                {
                  label: t(tBase + "kontrollkort"),
                  value: skuttDyr.Kontrollkort
                    ? skuttDyr.Kontrollkort
                    : t(tBase + "ikkeRegistrert"),
                  show: skuttDyr.Art === ArtType.Villrein,
                },
                {
                  label: t(tBase + "cwdProvesvar"),
                  value: skuttDyr.CWDPrøvesvar
                    ? skuttDyr.CWDPrøvesvar
                    : t(tBase + "ikkeRegistrert"),
                },
                {
                  label: t(tBase + "termisk"),
                  warning: skuttDyr.Termisk === null || skuttDyr.Termisk === undefined,
                  value:
                    skuttDyr.Termisk === true
                      ? t("pages.registrerArt.hjort.termisk.ja")
                      : skuttDyr.Termisk === false
                        ? t("pages.registrerArt.hjort.termisk.nei")
                        : t(tBase + "ikkeRegistrert"),
                  show: skuttDyr.Art === ArtType.Hjort,
                },
                {
                  label: t(tBase + "eksaktAlder"),
                  value: skuttDyr.EksaktAlder + " år",
                  show: !!skuttDyr.EksaktAlder,
                },
                {
                  label: t(tBase + "melkIJuret"),
                  value: skuttDyr.MelkIJuret ? "Ja" : "Nei",
                  show:
                    skuttDyr.Kjønn === KjønnType.Hunn &&
                    !(skuttDyr.MelkIJuret === null || skuttDyr.MelkIJuret === undefined),
                },
                {
                  label: t(tBase + "gevirtakker"),
                  value:
                    (skuttDyr.GevirtakkerVenstre ?? "") + " - " + (skuttDyr.GevirtakkerHøyre ?? ""),
                  show:
                    (skuttDyr.Alder === AlderType.Voksen || skuttDyr.Alder === AlderType.Ungdyr) &&
                    (skuttDyr.Kjønn === KjønnType.Hann ||
                      (skuttDyr.Kjønn === KjønnType.Hunn && skuttDyr.Art === ArtType.Villrein)) &&
                    (!!skuttDyr.GevirtakkerVenstre || !!skuttDyr.GevirtakkerHøyre),
                },
                {
                  label: t(tBase + "fellingssted"),
                  value: skuttDyr.Fellingssted,
                  show: !!skuttDyr.Fellingssted,
                },
                {
                  label: t(tBase + "registrertAv"),
                  value:
                    skuttDyr.RegistrertAv && skuttDyr.RegistrertDato
                      ? `${skuttDyr.RegistrertAv}, ${toStringWithFullMonth(
                          skuttDyr.RegistrertDato,
                        )}`
                      : "?",
                },
                {
                  label: t(tBase + "sistEndretAv"),
                  value: skuttDyr.SistEndretAv
                    ? `${skuttDyr.SistEndretAv}, ${toStringWithFullMonth(skuttDyr.SistEndretDato)}`
                    : "?",
                  show: !!skuttDyr.SistEndretAv && !!skuttDyr.SistEndretDato,
                },
                {
                  label: t(tBase + "merknad"),
                  value: <ShowMoreToggle limit={50}>{skuttDyr.Merknad}</ShowMoreToggle>,
                  show: !!skuttDyr.Merknad,
                },
                {
                  label: t(tBase + "status"),
                  value: (
                    <div className="flex mt-1 items-start">
                      <CompleteIcon className="mr-2" />
                      <Text bold variant={TextVariant.large}>
                        {t(tBase + "overførtTilHVR")}
                      </Text>
                    </div>
                  ),
                  show: !storedIndivid?.isPending,
                },
              ]}
            >
              <></>
            </SoSCard>

            {deleteDialog.element}
          </>
        )}
      </div>
      {canEditIndivid.value && (
        <BottomBarDouble
          buttonOne={
            <ButtonDefault
              id="SkuttDyrDetaljerSlett"
              label={t("actions.delete")}
              variant={ButtonVariant.Tertiary}
              onClick={handleDeleteClick}
              icon={<DeleteIcon />}
            />
          }
          buttonTwo={
            skuttDyr?.Id ? (
              <ButtonDefault
                id="SkuttDyrDetaljerEndre"
                label={t("actions.endre")}
                variant={ButtonVariant.Primary}
                onClick={endreIndivid}
                icon={<EditIcon />}
              />
            ) : (
              <></>
            )
          }
        />
      )}
    </div>
  );
}
